import type { OfflineContextFactory, RenderAudio } from '../audio/render';
import { sessionDuration } from '../domain/avOps';
import type { AvSession, Project } from '../domain/schemas';
import { Observable } from '../lib/store';
import { ANALYSIS_VERSION, AnalysisUnavailable, analysisSeconds, analyzeSession, type AudioFeatures } from './analysis';

/**
 * Cache of measured audio features (Phase 4C), shared by the live preview and video
 * export so both read the same analysis object.
 *
 * **Key.** Everything the rendered music depends on: patterns, arrangement, tracks and
 * samples, tempo, swing, master volume, clips, the audio bus, asset metadata (an asset id
 * always names the same bytes: blobs are only ever added under fresh ids), the session's
 * music source and length, and ANALYSIS_VERSION, serialised to JSON. It is compared by
 * value, so a frozen export snapshot of unchanged music finds the preview's result.
 *
 * **Memo.** `keyFor`/`peek` run on every preview frame, so the key is memoised on the
 * identity of the project and session objects (two reference checks per frame). Project
 * updates are pure (`ProjectSession.update` replaces the root on every edit), so a new
 * root re-derives the key by value. `ensure` never uses the memo: a request is always
 * keyed by value, so even an in-place mutation that bypassed the ops cannot be served a
 * stale analysis by a request, and the per-frame memo follows the request's key.
 *
 * **Ownership.** One computation per key, shared by every consumer that asks for it.
 * A consumer's `signal` cancels only that consumer's wait. The computation itself is
 * cancelled when its last consumer has left (all their signals aborted), or by `clear()`.
 * A request without a signal holds the computation until it finishes. A request for
 * different music never cancels a computation someone still waits for, so a live edit
 * cannot break an export that is preparing its analysis. The preview and one export can
 * therefore run at most two computations at a time.
 *
 * **Status.** `state` describes the most recent request. Results that finish later for
 * other keys are cached but never overwrite it. A cancelled or cleared computation never
 * caches its result.
 *
 * **Failures.** "Unavailable" (too long, nothing to render) is deterministic and kept.
 * Other failures are kept too, so the preview does not retry on every edit, but
 * `ensure(..., { retry: true })` tries again (the Retry button and every export).
 *
 * At most two results are kept (about 7.5 MB each for 5 minutes); evicted and cleared
 * results are released to the garbage collector.
 */

export type AnalysisStatus = 'idle' | 'running' | 'ready' | 'unavailable' | 'error';

export interface AnalysisState {
  key: string | null;
  status: AnalysisStatus;
  progress: number;
  message?: string;
  /** Analysed seconds and the time it took (render + analysis). */
  seconds?: number;
  renderMs?: number;
  analyzeMs?: number;
}

interface Run {
  key: string;
  promise: Promise<AudioFeatures | null>;
  controller: AbortController;
  /** Consumers waiting with a signal that has not aborted. */
  waiters: number;
  /** A consumer without a signal: the run cannot be cancelled except by clear(). */
  pinned: boolean;
  progress: number;
}

const CACHE_SIZE = 2;
const abortError = (message: string) => new DOMException(message, 'AbortError');

export class AnalysisStore {
  readonly state = new Observable<AnalysisState>({ key: null, status: 'idle', progress: 0 });
  private readonly cache = new Map<string, AudioFeatures>();
  private readonly unavailable = new Map<string, string>();
  private readonly failed = new Map<string, string>();
  private readonly runs = new Map<string, Run>();
  private memo: { project: Project; session: AvSession; key: string } | null = null;
  /** The key of the most recent request; only it may change `state`. */
  private focus: string | null = null;
  /** Analyses actually computed (cache misses), and computations started, for tests and diagnostics. */
  computed = 0;
  started = 0;

  constructor(
    private readonly audio?: RenderAudio,
    private readonly createContext?: OfflineContextFactory,
  ) {}

  /** The cache key of a session's music (memoised per project/session object for per-frame use). */
  keyFor(project: Project, session: AvSession): string {
    const m = this.memo;
    if (m && m.project === project && m.session === session) return m.key;
    return this.remember(project, session, valueKey(project, session));
  }

  /** The cached features of a session's music, or null (never computes). */
  peek(project: Project, session: AvSession): AudioFeatures | null {
    return this.cache.get(this.keyFor(project, session)) ?? null;
  }

  /** Why the analysis is unavailable for this music (too long, failed), or null. */
  unavailableReason(project: Project, session: AvSession): string | null {
    const key = this.keyFor(project, session);
    return this.unavailable.get(key) ?? this.failed.get(key) ?? null;
  }

  /** True when the analysis of this music failed and can be tried again. */
  canRetry(project: Project, session: AvSession): boolean {
    return this.failed.has(this.keyFor(project, session));
  }

  /**
   * Returns the features, computing them if needed (one computation per key at a time).
   * Resolves to null when the music cannot be analysed (too long, nothing to render, or a
   * failure; `retry` tries a failed analysis again); rejects only when `signal` aborts or
   * the store is cleared.
   */
  async ensure(project: Project, session: AvSession, signal?: AbortSignal, options: { retry?: boolean } = {}): Promise<AudioFeatures | null> {
    signal?.throwIfAborted();
    const key = this.remember(project, session, valueKey(project, session));
    this.focus = key;
    const hit = this.cache.get(key);
    if (hit) {
      this.touch(key, hit);
      this.state.set(readyState(key, hit));
      return hit;
    }
    const reason = this.unavailable.get(key);
    if (reason !== undefined) {
      this.state.set({ key, status: 'unavailable', progress: 0, message: reason });
      return null;
    }
    const failure = this.failed.get(key);
    if (failure !== undefined) {
      if (!options.retry) {
        this.state.set({ key, status: 'error', progress: 0, message: failure });
        return null;
      }
      this.failed.delete(key);
    }
    const run = this.runs.get(key) ?? this.start(key, project, session);
    if (this.runs.get(key) === run && this.state.get().key !== key) this.state.set({ key, status: 'running', progress: run.progress });
    if (!signal) {
      run.pinned = true;
      return run.promise;
    }
    run.waiters++;
    return new Promise<AudioFeatures | null>((resolve, reject) => {
      let done = false;
      const leave = () => {
        if (done) return false;
        done = true;
        signal.removeEventListener('abort', onAbort);
        run.waiters--;
        return true;
      };
      const onAbort = () => {
        if (!leave()) return;
        reject(signal.reason);
        this.release(run);
      };
      signal.addEventListener('abort', onAbort, { once: true });
      run.promise.then(
        (value) => leave() && resolve(value),
        (error) => leave() && reject(error),
      );
    });
  }

  /** Forgets everything and cancels every computation (project closed, app disposed). */
  clear(): void {
    for (const run of this.runs.values()) run.controller.abort(abortError('Cleared'));
    this.runs.clear();
    this.cache.clear();
    this.unavailable.clear();
    this.failed.clear();
    this.memo = null;
    this.focus = null;
    this.state.set({ key: null, status: 'idle', progress: 0 });
  }

  private remember(project: Project, session: AvSession, key: string): string {
    this.memo = { project, session, key };
    return key;
  }

  private start(key: string, project: Project, session: AvSession): Run {
    this.started++;
    const controller = new AbortController();
    const run: Run = { key, promise: Promise.resolve(null), controller, waiters: 0, pinned: false, progress: 0 };
    run.promise = this.compute(run, project, session);
    this.runs.set(key, run);
    run.promise
      .finally(() => {
        if (this.runs.get(key) === run) this.runs.delete(key);
      })
      .catch(() => undefined);
    return run;
  }

  /** Cancels a run whose last consumer has left (checked after the current task, so a consumer re-subscribing in the same tick keeps it). */
  private release(run: Run): void {
    queueMicrotask(() => {
      if (run.waiters > 0 || run.pinned || this.runs.get(run.key) !== run) return;
      run.controller.abort(abortError('No consumer is waiting for this analysis'));
      this.runs.delete(run.key);
    });
  }

  private async compute(run: Run, project: Project, session: AvSession): Promise<AudioFeatures | null> {
    const { key, controller } = run;
    const signal = controller.signal;
    const mine = () => this.focus === key;
    try {
      analysisSeconds(project, session); // throws AnalysisUnavailable early (too long, empty)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.unavailable.set(key, message);
      if (mine()) this.state.set({ key, status: 'unavailable', progress: 0, message });
      return null;
    }
    if (mine()) this.state.set({ key, status: 'running', progress: 0 });
    try {
      const features = await analyzeSession(project, session, {
        audio: this.audio,
        createContext: this.createContext,
        signal,
        onProgress: (f) => {
          run.progress = f;
          if (mine() && this.state.get().key === key) this.state.update({ progress: f });
        },
      });
      // A result that arrives after a cancel or clear() is dropped, never cached.
      signal.throwIfAborted();
      this.computed++;
      this.touch(key, features);
      if (mine()) this.state.set(readyState(key, features));
      return features;
    } catch (error) {
      if (signal.aborted) {
        const s = this.state.get();
        if (mine() && s.key === key && s.status === 'running') this.state.set({ key, status: 'idle', progress: 0 });
        throw signal.reason ?? error;
      }
      const message = error instanceof AnalysisUnavailable || error instanceof Error ? error.message : String(error);
      if (error instanceof AnalysisUnavailable) this.unavailable.set(key, message);
      else this.failed.set(key, message);
      if (mine()) this.state.set({ key, status: error instanceof AnalysisUnavailable ? 'unavailable' : 'error', progress: 0, message });
      return null;
    }
  }

  private touch(key: string, features: AudioFeatures): void {
    this.cache.delete(key);
    this.cache.set(key, features);
    while (this.cache.size > CACHE_SIZE) this.cache.delete(this.cache.keys().next().value!);
  }
}

function valueKey(project: Project, session: AvSession): string {
  const parts = [
    project.patterns,
    project.arrangement,
    project.tracks,
    project.settings.bpm,
    project.settings.swing,
    project.settings.masterVolumeDb,
    project.audioClips,
    project.audioBus,
    project.assets,
    session.music,
    sessionDuration(project, session),
  ];
  return JSON.stringify([ANALYSIS_VERSION, ...parts]);
}

const readyState = (key: string, f: AudioFeatures): AnalysisState => ({ key, status: 'ready', progress: 1, seconds: f.durationSec, renderMs: f.renderMs, analyzeMs: f.analyzeMs });
